import { useEffect, useState, useCallback } from 'react';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';
import axios from '../config/axios';
import Popup from "reactjs-popup";
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import "reactjs-popup/dist/index.css";
import "../style/user/users.css"


function Users(){
    const { t } = useTranslation();
    const [allUsers, setUsers] = useState([]);
    const [profiles, setProfiles] = useState([]);
    const gridApi = useGridApiRef();

    const fetchUsers = useCallback(() => {
        axios.get('/user')
            .then((response) => {
                setUsers(response.data);
            })
            .catch(error => console.error('users error:', error));
    }, []);

    const fetchProfiles = useCallback(() => {
        axios.get('/profile')
            .then((response) => {
                const profileData = response.data.profiles || response.data;
                setProfiles(Array.isArray(profileData) ? profileData : []);
            })
            .catch(error => console.error('profiles error:', error));
    }, []);

    useEffect(() => {
        fetchUsers();
        fetchProfiles();
    }, [fetchUsers, fetchProfiles]);

    const rows = allUsers.map(user => {
        const profile = profiles.find(p => p.userId === user.id);
        return {
            id: user.id,
            userIcon: profile?.photo || 'blogger.jpg',
            fullName: user.firstName + " " + user.lastName,
            role: user.authRole,
            email: user.email,
            dateCreated: new Date(user.created.slice(0 , 10)).toDateString().slice(4,15),
            dateSuspended: user.suspendedAt ? new Date(user.suspendedAt).toDateString().slice(4,15) : "-",
            isSuspended: user.suspended || false,
        };
    });

    const handleSuspend = (userId) => {
        axios.put(`/user/${userId}/suspend`)
            .then(() => {
                fetchUsers();
                alert("User has been suspended");
            })
            .catch((error) => {
                console.error(error);
                alert("Failed to suspend the user: " + (error.response?.data?.message || error.message));
            });
    };

    const handleReactivate = (userId) => {
        axios.put(`/user/${userId}/reactivate`)
            .then(() => {
                fetchUsers();
                alert("User has been reactivated");
            })
            .catch((error) => {
                console.error(error);
                alert("Failed to reactivate the user: " + (error.response?.data?.message || error.message));
            });
    };

    const handleDelete = (userId) => {
        axios.delete(`/user/${userId}`)
            .then(() => {
                fetchUsers();
                alert("User has been deleted");
            })
            .catch((error) => {
                console.error(error);
                alert("Failed to delete the user: " + (error.response?.data?.message || error.message));
            });
    };

    const columns = [
        {
            field: "userIcon",
            headerName: t('UsersPage.Icon'),
            width: 70,
            sortable: false,
            filterable: false,
            renderCell: (params) => <img className="data-grid-icon" alt="profile-pic" src={params.value || 'blogger.jpg'} style={{width: 80, height: 50, borderRadius: '50%', objectFit: 'cover'}} onError={(e) => { e.target.src = 'blogger.jpg'; }}/>
        },
        {
            field: "fullName",
            headerName: t('UsersPage.Username'),
            width: 240,
            renderCell: (params) => {
                return <Link to={`/profile/${params.row.id}`}>{params.value}{params.row.isSuspended && " (Suspended)"}</Link>
            }
        },
        {
            field: "role",
            headerName: t('UsersPage.Role'),
            width: 60,
            sortable: false
        },
        {
            field: "email",
            headerName: t('UsersPage.Email'),
            width: 250,
            sortable: false
        },
        {
            field: "dateCreated",
            headerName: t('UsersPage.Created'),
            width: 100,
            sortable: false
        },
        {
            field: "isSuspended",
            headerName: t('UsersPage.Actions'),
            width: 350,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                 return (
                 <div className="data-grid-actions">
                    <Popup trigger={<button disabled={params.value}>{t('UsersPage.Suspend')}</button>} modal contentStyle={{
                        height: '15%',
                        width: '40%'
                    }}>
                        {close => (
                        <div className="user-action">
                            <h3>{t('UsersPage.SuspendConfirm')}</h3>
                            <div className="button-column">
                                <button onClick={() => {
                                    handleSuspend(params.id);
                                    close();
                                }}>{t('UsersPage.Yes')}</button>
                                <button onClick={close}>{t('UsersPage.No')}</button>
                            </div>
                        </div>)}
                    </Popup>
                    <Popup trigger={<button disabled={!params.value}>{t('UsersPage.Reactivate')}</button>} modal contentStyle={{
                        height: '15%',
                        width: '40%'
                    }}>
                        {close => (
                        <div className="user-action">
                            <h3>{t('UsersPage.ReactivateConfirm')}</h3>
                                 <div className="button-column">
                                     <button onClick={() => {
                                        handleReactivate(params.id);
                                        close();
                                     }}>{t('UsersPage.Yes')}</button>
                                     <button onClick={close}>{t('UsersPage.No')}</button>
                                 </div>
                        </div>)}
                    </Popup>
                    <Popup trigger={<button className="delete-button">{t('UsersPage.Delete')}</button>} modal contentStyle={{
                        height: '15%',
                        width: '40%'
                    }}>
                        {close => (
                        <div className="user-action">
                            <h3>{t('UsersPage.DeleteConfirm')}</h3>
                            <div className="button-column">
                                <button onClick={() => {
                                    handleDelete(params.id);
                                    close();
                                }}>{t('UsersPage.Yes')}</button>
                                <button onClick={close}>{t('UsersPage.No')}</button>
                            </div>
                        </div>)}
                    </Popup>
                </div>)
            }
        }
    ]

   


    return(
        <div className="container">
            <h1 className="user-pagename">
                User Management Page
            </h1>
            <div className="user-container">
                <DataGrid
                    rows={rows}
                    columns={columns}
                    rowHeight={150}
                    pageSizeOptions={[5, 10, 25, 50, 100]}
                    apiRef={gridApi}
                />
            </div>
        </div>
    );
}

export default Users;
